import React, { FC, memo, MouseEvent, useCallback } from "react";
import { ButtonColor } from "enums/Button";
import { Map } from "immutable";
import { Hidden, Visible } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import { OutgoingComponentCommonProps } from "types/outgoing";
import ServiceLogo from "../../../../components/ServiceLogo/ServiceLogo";
import {
  getDetailedItem,
  updateAddress,
} from "../../../../reduxFolder/reducers/outgoing";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  panelSelectData,
  panelSelectOpened,
} from "../../../../reduxFolder/selectors/rightPanelSelectors";
import {
  OutgoingActionsCol,
  OutgoingCol,
  OutgoingGrowCol,
  OutgoingGrowText,
  OutgoingMainCol,
  OutgoingMetaCol,
} from "../../../../styles/outgoingStyles";
import { ParcelButton } from "../../../../styles/parcelsStyles";
import {
  DIMENSIONS_COL_WIDTH,
  SKU_COL_WIDTH,
  WEIGHT_COL_WIDTH,
} from "../rowLayout";
import { ParcelWrapper } from "./WaitingFor.styles";

interface WaitingForInformationProps extends OutgoingComponentCommonProps {
  updateAddress: (id: string, data: any) => Promise<void>;
  getDetailedItem: (id: string) => void;
}

const WaitingForInformation: FC<WaitingForInformationProps> = React.memo(
  ({
    parcel,
    units,
    updateAddress,
    openRightPanel,
    getDetailedItem,
    isPanelOpened,
    panelData,
    layoutTier = "full",
  }) => {
    const { t } = useTranslation("common");
    const deliveryName = parcel.get("preferred_carrier");
    const isSelectedShipment =
      isPanelOpened &&
      panelData &&
      panelData.get("detailedItemID") === parcel.get("id");
    const isEditCustomData = parcel.get("is_customs_data_invalid");
    const weight =
      parcel.get("weight") && Number(parcel.get("weight")).toFixed(2);
    const showWeightCol = layoutTier !== "narrow";
    const showDimsCol = layoutTier === "full";
    const openMoreInfo = useCallback(
      (event: MouseEvent, openTab?: string) => {
        event.stopPropagation();
        openRightPanel(
          "WAITING_PAYMENT",
          Map({
            detailedItemID: parcel.get("id"),
            excessItems: parcel.get("excess_items"),
            openTab: openTab,
            isEditCustomData,
          }),
        );
      },
      [openRightPanel, parcel, isEditCustomData],
    );

    const clickAddIdInformation = useCallback(
      (id: string) => (event: MouseEvent) => {
        event.stopPropagation();
        getDetailedItem(id);
        openRightPanel(
          "ADDRESS_FORM",
          Map({
            isEditForm: true,
            detailedItemID: id,
            openTab: "Custom ID",
            addressID: id,
            updateAddress,
          }),
        );
      },
      [updateAddress, getDetailedItem, openRightPanel],
    );

    const clickProhibitedGoodsFound = useCallback(
      (event: MouseEvent) => {
        event.stopPropagation();
        openRightPanel("PROHIBITED_FOUND");
      },
      [openRightPanel],
    );

    const renderActionButtons = () => (
      <>
        {parcel.get("is_passport_invalid") && (
          <ParcelButton
            color={ButtonColor.Primary}
            onClick={clickAddIdInformation(parcel.get("id"))}
          >
            {t("common.addIdInformation")}
          </ParcelButton>
        )}
        {parcel.get("is_prohibited_goods") && (
          <ParcelButton
            color={ButtonColor.Primary}
            onClick={clickProhibitedGoodsFound}
          >
            {t("common.prohibitedGoodsFound")}
          </ParcelButton>
        )}
        {isEditCustomData && (
          <ParcelButton
            color={ButtonColor.Primary}
            onClick={(event: MouseEvent) => openMoreInfo(event, "Customs data")}
          >
            {t("common.editCustomsData")}
          </ParcelButton>
        )}
      </>
    );

    return (
      <ParcelWrapper
        selected={isSelectedShipment}
        onClick={(event) => openMoreInfo(event)}
      >
        <Visible xs>
          <OutgoingCol xs={2}>{parcel.get("sku")}</OutgoingCol>
          <OutgoingCol xs={6}>
            <ServiceLogo serviceKey={deliveryName} />
            {deliveryName}
          </OutgoingCol>
          <OutgoingCol xs={4}>
            {parcel.get("width")}
            <span>x</span>
            {parcel.get("height")}
            <span>x</span>
            {parcel.get("depth")} {t(`units.${units}.secondaryShort`)}
          </OutgoingCol>
          <OutgoingCol xs={2}>
            {`${weight} ${t(`units.${units}.primaryShort`)}`}
          </OutgoingCol>
          <OutgoingCol xs={10}>{renderActionButtons()}</OutgoingCol>
        </Visible>
        <Hidden xs>
          <OutgoingMainCol sm={12}>
            <OutgoingMetaCol $width={SKU_COL_WIDTH} $grow={0}>
              {parcel.get("sku")}
            </OutgoingMetaCol>
            <OutgoingGrowCol>
              <ServiceLogo serviceKey={deliveryName} />
              <OutgoingGrowText>{deliveryName}</OutgoingGrowText>
            </OutgoingGrowCol>
            {showDimsCol && (
              <OutgoingMetaCol $width={DIMENSIONS_COL_WIDTH} $grow={1.5}>
                {parcel.get("width")}
                <span>x</span>
                {parcel.get("height")}
                <span>x</span>
                {parcel.get("depth")} {t(`units.${units}.secondaryShort`)}
              </OutgoingMetaCol>
            )}
            {showWeightCol && (
              <OutgoingMetaCol $width={WEIGHT_COL_WIDTH}>
                {`${weight} ${t(`units.${units}.primaryShort`)}`}
              </OutgoingMetaCol>
            )}
            <OutgoingActionsCol>{renderActionButtons()}</OutgoingActionsCol>
          </OutgoingMainCol>
        </Hidden>
      </ParcelWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    isPanelOpened: panelSelectOpened,
    panelData: panelSelectData,
  }),
  {
    updateAddress,
    openRightPanel,
    getDetailedItem,
  },
);

export default withConnect(WaitingForInformation);
